import { useCallback, useEffect, useRef, useState } from "react";
import { downloadAssetArchive, errorMessage, triggerBrowserDownload } from "@/lib/api";
import { zipPartName, zipParts, type DownloadEntry } from "@/lib/selection";

export interface BulkDownloadState {
  running: boolean;
  done: number;
  total: number;
  failed: number;
  /** 1-based ZIP part being prepared, and how many parts the selection needs. */
  part?: number;
  parts?: number;
  error?: string;
}

const IDLE: BulkDownloadState = { running: false, done: 0, total: 0, failed: 0 };

/** Fetch each complete authorized archive before triggering exactly one save per part. */
export function useBulkDownload() {
  const [state, setState] = useState<BulkDownloadState>(IDLE);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const start = useCallback(async (entries: DownloadEntry[]) => {
    if (runningRef.current || entries.length === 0) return;
    runningRef.current = true;
    const controller = new AbortController();
    controllerRef.current = controller;
    const parts = zipParts(entries);
    let done = 0;
    let part = 0;
    try {
      for (const chunk of parts) {
        part += 1;
        setState({ running: true, done, total: entries.length, failed: 0, part, parts: parts.length });
        const blob = await downloadAssetArchive(chunk, controller.signal);
        if (!mountedRef.current || controller.signal.aborted) return;
        const url = URL.createObjectURL(blob);
        try {
          triggerBrowserDownload(url, zipPartName(part, parts.length));
        } finally {
          // Keep the URL alive long enough for browsers to consume the click.
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        }
        done += chunk.length;
      }
      setState({ running: false, done, total: entries.length, failed: 0, parts: parts.length });
    } catch (cause) {
      if (mountedRef.current && !controller.signal.aborted) {
        const message = errorMessage(cause, "ZIP could not be downloaded. Please try again.");
        setState({ running: false, done, total: entries.length, failed: entries.length - done, part, parts: parts.length,
          error: parts.length > 1 ? `ZIP ${part} of ${parts.length} failed after ${part - 1} saved. ${message}` : message });
      }
    } finally {
      runningRef.current = false;
      controllerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    if (!runningRef.current) setState(IDLE);
  }, []);

  return { start, reset, state };
}
